/**
 * Data model: the class as a UML class diagram, after rinf-describe's "Data model" tab. The class
 * box in the middle with its attributes (name : type [multiplicity]) from the shape's validated
 * rows; associations out to the classes it points at on the right, and in from the classes that
 * point at this record on the left. A filled dot is an attribute this record has.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { uriToDereferencePath } from '@utils/prefix.utils';
import { CopyButton } from '@bn-apps/dim-ui-react';
import { shortenUri } from '@hooks/useEntity';
import type { EntityViewModel } from '@hooks/useEntityView';
import type { IncomingGroup } from '@services/entityView.service';
import { pickLang, type ViewPropertyShape } from '@utils/viewShapes.utils';
import { localName } from '../linearLocation';
import { prettyPath } from '../page';
import type { WidgetContext } from '../widgets/types';
import styles from '../EntityTemplate.module.scss';

const multiplicity = (property: ViewPropertyShape) => {
  const min = property.minCount ?? 0;
  const max = property.maxCount === null ? '*' : String(property.maxCount);
  return min === Number(max) ? max : `${min}..${max}`;
};

const typeOf = (property: ViewPropertyShape) =>
  property.datatypes.map(localName).join(' | ') || (property.classIri ? localName(property.classIri) : property.nodeKind ? localName(property.nodeKind) : 'IRI');

interface DataModelProps {
  model: EntityViewModel;
  context: WidgetContext;
  incoming: IncomingGroup[];
}

const DataModel = ({ model, context, incoming }: DataModelProps): JSX.Element | null => {
  const { t } = useTranslation();
  const [allIn, setAllIn] = useState(false);
  const shape = model.nodeShapes[0];
  if (!shape) return null;
  const name = pickLang(shape.label, context.language) ?? localName(shape.id);
  // The schema: what validates, not the page's own view rows.
  const rows = model.allProperties.filter((property) => !property.deactivated && !property.hidden);
  const attributes = rows.filter((property) => !property.classIri);
  const associations = rows.filter((property) => property.classIri);
  const has = (property: ViewPropertyShape) => (model.values[property.id]?.length ?? 0) > 0;
  const into = Array.from(new Map(incoming.map((group) => [`${group.classIri} ${group.predicate}`, group])).values());

  const mermaid = [
    'classDiagram',
    `  class ${localName(shape.targetClasses[0] ?? shape.id)} {`,
    ...attributes.map((property) => `    ${prettyPath(property, context.prefixes).replace(/^\w+:/, '')} : ${typeOf(property)} [${multiplicity(property)}]`),
    '  }',
    ...associations.map(
      (property) => `  ${localName(shape.targetClasses[0] ?? shape.id)} --> "${multiplicity(property)}" ${localName(property.classIri!)} : ${localName(prettyPath(property, context.prefixes))}`
    ),
    ...into.map((group) => `  ${localName(group.classIri)} --> ${localName(shape.targetClasses[0] ?? shape.id)} : ${localName(group.predicate)}`),
  ].join('\n');

  return (
    <div className={styles.model}>
      <div className={styles.modelSide}>
        <p className={styles.modelCaption}>{t('entity.template.model.incoming', { count: into.length })}</p>
        {(allIn ? into : into.slice(0, 8)).map((group) => (
          <div key={`${group.classIri} ${group.predicate}`} className={styles.modelLink} data-side="in">
            <Link to={uriToDereferencePath(group.classIri)} className={styles.modelTarget} title={group.classIri}>
              {localName(group.classIri)}
            </Link>
            <span className={styles.modelArrow}>
              {shortenUri(group.predicate, context.prefixes)} → · {t('entity.template.model.here', { count: group.count })}
            </span>
          </div>
        ))}
        {into.length > 8 && (
          <button type="button" className={styles.inlineLink} aria-expanded={allIn} onClick={() => setAllIn(!allIn)}>
            {allIn ? t('entity.template.facts.showFewer') : t('entity.template.model.more', { count: into.length - 8 })}
          </button>
        )}
      </div>

      <div className={styles.modelClass} data-in={into.length > 0 || undefined} data-out={associations.length > 0 || undefined}>
        <div className={styles.modelHead}>
          <Link to={uriToDereferencePath(shape.targetClasses[0] ?? shape.id)} className={styles.modelName}>
            {name}
          </Link>
          <code>{shape.targetClasses.map((iri) => shortenUri(iri, context.prefixes)).join(', ')}</code>
        </div>
        <ul>
          {attributes.map((property) => (
            <li key={property.id} title={pickLang(property.description, context.language) ?? undefined}>
              <span className={styles.modelDot} data-filled={has(property) || undefined} aria-label={has(property) ? t('entity.template.model.filled') : undefined} />
              <code>{prettyPath(property, context.prefixes)}</code>
              <span className={styles.modelType}>{typeOf(property)}</span>
              <span className={styles.modelCard}>[{multiplicity(property)}]</span>
            </li>
          ))}
        </ul>
        <div className={styles.modelFoot}>
          <span>
            <span className={styles.modelDot} data-filled /> {t('entity.template.model.legend')}
          </span>
          <CopyButton text={mermaid} label={t('entity.template.model.copyMermaid')} copiedLabel={t('entity.template.about.copied')} size="xs" />
        </div>
      </div>

      <div className={styles.modelSide}>
        <p className={styles.modelCaption}>{t('entity.template.model.links', { count: associations.length })}</p>
        {associations.map((property) => (
          <div key={property.id} className={styles.modelLink} data-side="out">
            <span className={styles.modelArrow}>
              <span className={styles.modelDot} data-filled={has(property) || undefined} /> {prettyPath(property, context.prefixes)} [{multiplicity(property)}] →
            </span>
            <Link to={uriToDereferencePath(property.classIri!)} className={styles.modelTarget} title={property.classIri!}>
              {localName(property.classIri!)}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DataModel;
